/**
 * 🚀 iSendInbox React App
 * Hauptkomponente für das moderne Mailing System
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import SmtpServers from './components/SMTP/SmtpServers';
import WebmailerAccounts from './components/Webmailer/WebmailerAccounts';
import Templates from './components/Templates/Templates';
import MailJobs from './components/Jobs/MailJobs';
import JobDetails from './components/Jobs/JobDetails';
import MultiCampaignDetails from './components/MultiCampaign/MultiCampaignDetails';
import CidLogos from './components/CID/CidLogos';
import Settings from './components/Settings/Settings';
import UserManagement from './components/Admin/UserManagement';

// Services
import { AuthProvider, useAuth } from './services/AuthService';
import { ThemeProvider } from './context/ThemeContext';  // 🌙 Dark-Mode

// Styles
import './App.css';

// React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/smtp" element={
                <ProtectedRoute>
                  <Layout>
                    <SmtpServers />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/webmailer" element={
                <ProtectedRoute>
                  <Layout>
                    <WebmailerAccounts />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/templates" element={
                <ProtectedRoute>
                  <Layout>
                    <Templates />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/jobs" element={
                <ProtectedRoute>
                  <Layout>
                    <MailJobs />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/jobs/:jobId" element={
                <ProtectedRoute>
                  <Layout>
                    <JobDetails />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/multi-campaigns/:campaignId" element={
                <ProtectedRoute>
                  <Layout>
                    <MultiCampaignDetails />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/cid-logos" element={
                <ProtectedRoute>
                  <Layout>
                    <CidLogos />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/users" element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </AdminRoute>
                </ProtectedRoute>
              } />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
